import Axios from "axios";
import { Formik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { Creators as CattleProducerActions } from "../../../store/ducks/cattleProducer";
import {
  Container,
  Footer,
  PropertyCard,
  PropertyHeader,
  PropertyTitle,
} from "./styles";
/* Components */
import { Button, Col, Input, notification, Row, Spin } from "antd";
import ButtonStandard from "../../utils/button";
import DocumentInput from "../../utils/input/documentInput";
/* Services */
import { getCattleProducerShow } from "../../../services/cattleProducerService";
import {
  cnpjMask,
  cpfMask,
  getOnlyNumber,
  validateCNPJ,
  validateCPF,
} from "../../../services/helpersMethodsService";
import { logEventError, logEventInfo } from "../../../utils/logger";

const DrawerCattleProducer = () => {
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const {
    drawerVisible,
    isLoadingRequest: isLoadingRequestRedux,
    drawerActionType,
    idCattleProducerEdit,
  } = useSelector((state) => state.cattleProducer);

  const dispatch = useDispatch();

  const [formCattleProducer, setFormCattleProducer] = useState({
    id: null,
    groupId,
    farmId,
    name: null,
    document: "",
    properties: [],
  });
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);

  const formikRef = useRef(null);
  const signalRef = useRef(Axios.CancelToken.source());

  const closeDrawer = useCallback(() => {
    if (signalRef.current) {
      signalRef.current.cancel();
      signalRef.current = Axios.CancelToken.source();
    }
    const resetForm = {
      id: null,
      groupId,
      farmId,
      name: null,
      document: "",
      properties: [],
    };
    setFormCattleProducer(resetForm);
    setIsLoadingRequest(false);
    if (formikRef.current) {
      formikRef.current.resetForm(resetForm);
    }
    dispatch(CattleProducerActions.hideDrawer());
  }, [dispatch, groupId, farmId]);

  const validateForm = useCallback(
    (values) => {
      // Validate document before processing
      if (values.document !== null && values.document.trim() !== "") {
        const documentOnlyNumbers = getOnlyNumber(values.document);
        if (documentOnlyNumbers.length <= 11) {
          if (!validateCPF(values.document)) {
            notification.error({
              message: translation.cattleProducer.form.validation.cpfError,
              description:
                translation.cattleProducer.form.validation.cpfErrorDescription,
            });

            throw new Error(
              translation.cattleProducer.form.validation.cpfError
            );
          }
        } else if (documentOnlyNumbers.length > 11) {
          if (!validateCNPJ(values.document)) {
            notification.error({
              message: translation.cattleProducer.form.validation.cnpjError,
              description:
                translation.cattleProducer.form.validation.cnpjErrorDescription,
            });

            throw new Error(
              translation.cattleProducer.form.validation.cnpjError
            );
          }
        }
      }

      // Validate properties documents
      if (values.properties && values.properties.length > 0) {
        for (let i = 0; i < values.properties.length; i++) {
          const property = values.properties[i];
          if (property.document !== null && property.document.trim() !== "") {
            const propertyDocOnlyNumbers = getOnlyNumber(property.document);
            if (propertyDocOnlyNumbers.length <= 11) {
              if (!validateCPF(property.document)) {
                notification.error({
                  message:
                    translation.cattleProducer.form.validation.propertyCpfError,
                  description:
                    translation.cattleProducer.form.validation
                      .propertyCpfErrorDescription,
                });

                throw new Error(
                  translation.cattleProducer.form.validation.propertyCpfError
                );
              }
            } else if (propertyDocOnlyNumbers.length > 11) {
              if (!validateCNPJ(property.document)) {
                notification.error({
                  message:
                    translation.cattleProducer.form.validation
                      .propertyCnpjError,
                  description:
                    translation.cattleProducer.form.validation
                      .propertyCnpjErrorDescription,
                });

                throw new Error(
                  translation.cattleProducer.form.validation.propertyCnpjError
                );
              }
            }
          }
        }
      }

      // Process document after validation
      if (values.document !== null) {
        const document = getOnlyNumber(values.document);
        values.document = document;
      } else {
        values.document = null;
      }

      // Process properties documents
      if (values.properties && values.properties.length > 0) {
        values.properties = values.properties.map((property) => {
          const propertyCopy = { ...property };
          if (
            propertyCopy.document !== null &&
            propertyCopy.document.trim() !== ""
          ) {
            propertyCopy.document = getOnlyNumber(propertyCopy.document);
          } else {
            propertyCopy.document = null;
          }
          return propertyCopy;
        });
      }
    },
    [translation]
  );

  const handleNewCattleProducer = useCallback(
    (cattleProducer) => {
      try {
        dispatch(
          CattleProducerActions.saveCattleProducer(
            groupId,
            farmId,
            null,
            cattleProducer
          )
        );
      } catch (error) {}
    },
    [dispatch, groupId, farmId]
  );

  const handleUpdateCattleProducer = useCallback(
    (cattleProducer, id) => {
      try {
        dispatch(
          CattleProducerActions.saveCattleProducer(
            groupId,
            farmId,
            id,
            cattleProducer
          )
        );
      } catch (error) {}
    },
    [dispatch, groupId, farmId]
  );

  const handleSubmitModalForm = useCallback(
    (values, { setSubmitting, setErrors, setStatus, resetForm }) => {
      try {
        validateForm(values);
        if (values.id === null) {
          handleNewCattleProducer(values);
        } else {
          handleUpdateCattleProducer(values, values.id);
        }
      } catch (error) {
        throw new Error(error);
      }
    },
    [validateForm, handleNewCattleProducer, handleUpdateCattleProducer]
  );

  const addProperty = useCallback((props) => {
    const newProperties = [
      ...(props.values.properties || []),
      { name: "", document: "" },
    ];
    props.setValues({
      ...props.values,
      properties: newProperties,
    });
  }, []);

  const removeProperty = useCallback((index, props) => {
    const newProperties = props.values.properties.filter((_, i) => i !== index);
    props.setValues({
      ...props.values,
      properties: newProperties,
    });
  }, []);

  const handleDrawerVisible = useCallback(
    async (visible) => {
      if (visible) {
        setIsLoadingRequest(true);
        logEventInfo("DrawerCattleProducer - handleDrawerVisible", visible);
        logEventInfo(
          "DrawerCattleProducer - drawerActionType",
          drawerActionType
        );
        logEventInfo(
          "DrawerCattleProducer - idCattleProducerEdit",
          idCattleProducerEdit
        );
        if (drawerActionType === "edit" && idCattleProducerEdit) {
          try {
            const response = await getCattleProducerShow({
              groupId,
              farmId,
              id: idCattleProducerEdit,
              signal: signalRef.current.token,
            });

            const cattleProducerData =
              response?.data?.results || response?.data || {};

            if (
              cattleProducerData &&
              Object.keys(cattleProducerData).length > 0
            ) {
              const formData = {
                id: cattleProducerData.id,
                groupId,
                farmId,
                name: cattleProducerData.name || null,
                document: cattleProducerData.document || "",
                properties: cattleProducerData.properties || [],
              };

              // Format documents for display
              if (formData.document) {
                const docOnlyNumbers = getOnlyNumber(formData.document);
                formData.document =
                  docOnlyNumbers.length <= 11
                    ? cpfMask(docOnlyNumbers)
                    : cnpjMask(docOnlyNumbers);
              }

              if (formData.properties && formData.properties.length > 0) {
                formData.properties = formData.properties.map((prop) => {
                  const propCopy = { ...prop };
                  if (propCopy.document) {
                    const docOnlyNumbers = getOnlyNumber(propCopy.document);
                    propCopy.document =
                      docOnlyNumbers.length <= 11
                        ? cpfMask(docOnlyNumbers)
                        : cnpjMask(docOnlyNumbers);
                  }
                  return propCopy;
                });
              }

              setFormCattleProducer(formData);
              setIsLoadingRequest(false);
              if (formikRef.current) {
                formikRef.current.resetForm(formData);
              }
            }
          } catch (error) {
            logEventError(
              "DrawerCattleProducer - getCattleProducerShow",
              error
            );
            setIsLoadingRequest(false);
          }
        } else {
          const resetForm = {
            id: null,
            groupId,
            farmId,
            name: null,
            document: "",
            properties: [],
          };
          setFormCattleProducer(resetForm);
          setIsLoadingRequest(false);
          if (formikRef.current) {
            formikRef.current.resetForm(resetForm);
          }
        }
      }
    },
    [drawerActionType, idCattleProducerEdit, groupId, farmId]
  );

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(
      translation.cattleProducer.form.validation.nameRequired
    ),
    properties: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(),
      })
    ).min(1),
  });

  useEffect(() => {
    return () => {
      if (signalRef.current) {
        signalRef.current.cancel("Component unmounted");
      }
    };
  }, []);

  return (
    <Container
      title={
        drawerActionType === "edit"
          ? translation.cattleProducer.drawer.titleEdit
          : translation.cattleProducer.drawer.title
      }
      width={720}
      onClose={closeDrawer}
      visible={drawerVisible}
      destroyOnClose
      maskClosable={false}
      afterVisibleChange={handleDrawerVisible}
    >
      <Spin spinning={isLoadingRequest || isLoadingRequestRedux}>
        <Formik
          ref={formikRef}
          initialValues={formCattleProducer}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmitModalForm}
          render={(props) => (
            <form onSubmit={props.handleSubmit} autoComplete="off">
              <div className="drawerForm">
                {Object.entries(props.errors).length > 0 &&
                  props.submitCount > 0 && (
                    <Row type="flex" justify="center" align="middle">
                      <label className="error">
                        {translation.error.formError}
                      </label>
                    </Row>
                  )}
                {/* Name */}
                <Row type="flex" style={{ marginTop: 13 }} align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label
                        className={
                          props.errors.name && props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.cattleProducer.form.name} *
                      </label>
                    </Row>
                    <Row>
                      <Input
                        name="name"
                        value={props.values.name || ""}
                        type="text"
                        autoComplete="chrome-off"
                        placeholder={translation.defaultPlaceholder}
                        onChange={(e) => {
                          props.setFieldValue("name", e.target.value);
                          props.setFieldError("name", null);
                        }}
                      />
                    </Row>
                  </Col>
                </Row>

                {/* Document */}
                <Row type="flex" style={{ marginTop: 13 }} align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label>{translation.cattleProducer.form.document}</label>
                    </Row>
                    <Row type="flex" justify="start" gutter={4} align="middle">
                      <Col span={16}>
                        <DocumentInput
                          kind="mixed"
                          placeholder={
                            translation.cattleProducer.form.documentPlaceHolder
                          }
                          value={props.values.document}
                          name="document"
                          autoComplete="chrome-off"
                          onChange={(e) => {
                            props.setFieldValue("document", e.target.value);
                          }}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {/* Properties Section */}
                <Row type="flex" style={{ marginTop: 24 }}>
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label className={props.errors.properties && props.submitCount > 0 ? "error" : ""}>
                        {translation.cattleProducer.form.properties}*
                      </label>
                    </Row>
                    {props.values.properties &&
                      props.values.properties.map((property, index) => (
                        <PropertyCard key={index}>
                          <PropertyHeader>
                            <PropertyTitle>
                              {translation.cattleProducer.form.property} #
                              {index + 1}
                            </PropertyTitle>
                            <Button
                              type="danger"
                              icon="delete"
                              size="small"
                              onClick={() => removeProperty(index, props)}
                            >
                              {translation.table.menu.delete}
                            </Button>
                          </PropertyHeader>
                          <Row type="flex" gutter={16}>
                            <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                              <Row>
                                <label
                                  className={
                                    props.errors.properties &&
                                    props.errors.properties[index] &&
                                    props.errors.properties[index].name
                                      ? "error"
                                      : ""
                                  }
                                >
                                  {translation.cattleProducer.form.propertyName}{" "}
                                  *
                                </label>
                              </Row>
                              <Row>
                                <Input
                                  value={property.name || ""}
                                  placeholder={translation.defaultPlaceholder}
                                  onChange={(e) => {
                                    const newProperties = [
                                      ...props.values.properties,
                                    ];
                                    newProperties[index].name = e.target.value;
                                    props.setFieldValue(
                                      "properties",
                                      newProperties
                                    );
                                  }}
                                />
                              </Row>
                            </Col>
                            <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                              <Row>
                                <label>
                                  {
                                    translation.cattleProducer.form
                                      .propertyDocument
                                  }
                                </label>
                              </Row>
                              <Row>
                                <DocumentInput
                                  kind="mixed"
                                  placeholder={
                                    translation.cattleProducer.form
                                      .documentPlaceHolder
                                  }
                                  value={property.document || ""}
                                  onChange={(e) => {
                                    const newProperties = [
                                      ...props.values.properties,
                                    ];
                                    newProperties[index].document =
                                      e.target.value;
                                    props.setFieldValue(
                                      "properties",
                                      newProperties
                                    );
                                  }}
                                />
                              </Row>
                            </Col>
                          </Row>
                        </PropertyCard>
                      ))}
                    <Row type="flex" style={{ marginTop: 16, width: "100%" }}>
                      <Col span={24}>
                        <Button
                          type="dashed"
                          icon="plus"
                          style={{ width: "100%" }}
                          onClick={() => addProperty(props)}
                        >
                          {translation.cattleProducer.form.addProperty}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
              <Footer>
                <div className="buttonsDiv">
                  <ButtonStandard
                    buttonType="type7"
                    type="button"
                    onClick={closeDrawer}
                  >
                    {translation.buttons.cancel}
                  </ButtonStandard>
                  <ButtonStandard buttonType="type6" type="submit">
                    {translation.buttons.save}
                  </ButtonStandard>
                </div>
              </Footer>
            </form>
          )}
        />
      </Spin>
    </Container>
  );
};

export default DrawerCattleProducer;
